/**
 * Error to Strings mapping
 */

import { ErrorObject } from '@thoughtspot/blink-context';
import i18n, { i18nKeys, translate as t } from '@thoughtspot/i18n';
import _ from 'lodash';
import { generateErrorParamMap } from './error-utils';

export interface LocalisedErrorMsg {
    summary: string;
    detail?: string;
    action?: string;
}

/**
 * Get Content for given message code
 *
 * The summary, action and detail message for each error code are
 * present in the translated string in the key msg_code.<code>.summary,
 * msg_code.<code>.action and msg_code.<code>.detail if it exists.
 * Now we don't don't if action, detail and summary exists for a given
 * error code. This is where we use the messageCodes json which is
 * generated from message_codes file in common/localization/. This file
 * will contain the action, detail and summary fields for an error code
 * if it existsj
 *
 * @param code Error code
 * @param defaultErrorMessage Default error message
 * - Used when `msg_code.<code>.summary` is not available.
 * - If this is also unavailable, `error.validation.default` is returned.
 */
function getTranslatedInfoForMessageCode(
    code?: unknown,
    defaultErrorMessage?: string,
    parameters?: string[],
): LocalisedErrorMsg {
    if (!code && !defaultErrorMessage)
        return {
            summary: undefined,
            action: undefined,
            detail: undefined,
        };

    const defaultMessage = defaultErrorMessage || 'error.validation.default';
    const fallbackKey = `msg_code.${code}.summary_fallback`;
    const summaryKey = `msg_code.${code}.summary`;

    // Use fallback if it exists and all parameters are empty/missing
    // For now only for 11038 code, the fallback key exists
    // Fallback key does not require any parameters
    const hasEmptyParams =
        !parameters?.length || parameters.every(param => !param);
    const useFallback = hasEmptyParams && i18n.exists(fallbackKey);

    // Determine summary key: fallback > summary > default
    let summary: string;
    if (useFallback) {
        summary = fallbackKey;
    } else if (i18n.exists(summaryKey)) {
        summary = summaryKey;
    } else {
        summary = defaultMessage;
    }

    return {
        summary,
        action: i18n.exists(`msg_code.${code}.action`)
            ? `msg_code.${code}.action`
            : undefined,
        detail: i18n.exists(`msg_code.${code}.detail`)
            ? `msg_code.${code}.detail`
            : undefined,
    };
}

/**
 * Get error message strings for given error.
 *   Returns 2 strings: `message` and `action`
 *
 * Uses `error.code` to get error message strings.
 *
 * - `message`: Returns error detail if available, else error summary.
 *   - Uses `msg_code.{code}.detail` and `msg_code.{code}.summary` strings.
 *   - If both are not available, `defaultErrorMessage` arg is returned.
 *   - If that is also unavailable, `error.validation.default` string is used.
 * - `action`: Returns error action if available.
 *   - Uses `msg_code.{code}.action` string.
 *
 * @param error Error object
 * @param defaultErrorMessage Default error message, see fn docs for more.
 * @param preferSummaryOverDetail Prefer `summary` for `message` even if `detail` is present.
 */
function getTranslatedStringsForMessageCode(
    error?: ErrorObject,
    defaultErrorMessage?: string,
    preferSummaryOverDetail = false,
): { message: string; action?: string } {
    if (!error?.code && !defaultErrorMessage)
        return {
            message: '',
            action: '',
        };

    const { code } = error;
    // Returns string names for all eror messages
    const translatedErrorInfo = getTranslatedInfoForMessageCode(
        code,
        defaultErrorMessage,
    );
    // Error msg interpolation variables
    const errorMsgInterpolationVars =
        error?.errorMappingContext ||
        generateErrorParamMap(error?.errorObj?.extensions?.parameters);

    // Error message
    const message =
        !preferSummaryOverDetail && translatedErrorInfo.detail
            ? t(
                  translatedErrorInfo.detail as i18nKeys,
                  errorMsgInterpolationVars,
              )
            : t(
                  translatedErrorInfo.summary as i18nKeys,
                  _.defaults(_.omitBy(errorMsgInterpolationVars, _.isNil), {
                      0: '',
                      1: '',
                  }),
              );

    // Error action message
    const action = t(translatedErrorInfo.action as i18nKeys);

    return {
        message,
        action,
    };
}

export { getTranslatedInfoForMessageCode, getTranslatedStringsForMessageCode };
